import React, { useEffect, useState } from 'react'
import ReactGA from 'react-ga'
import {
  Route,
  Switch,
  Redirect,
  useHistory,
  useLocation,
} from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'

import CircularProgress from '@material-ui/core/CircularProgress'

import CookieAdvice from 'containers/CookieAdvice'

import { initHotjar } from 'libs/hotjar'
import { initGA } from 'libs/ga'

import { getUser } from 'services/auth/selectors'
import { logout, checkActiveModule } from 'services/auth/actions'
import { redirectToLogin } from 'services/auth/keycloak-api'
import { initConfig } from 'services/config/actions'
import { getUnreadNotifications } from 'services/notifications/selectors'
import { getIsConfigLoaded } from 'services/config/selectors'

import HomePage from './pages/Home'
import Login from './pages/Login'
import NotFound from './pages/NotFound'
import ServerError from './pages/ServerError'
import Dashboard from './pages/Dashboard'
import Searches from './pages/Searches'
import Vendors from './pages/Vendors'
import Invitations from './pages/Invitations'
import VendorDetails from 'pages/VendorDetails'
import SearchDetails from 'pages/SearchDetails'
import HomePageAerce from './pages/external/HomeAerce'

import Box from '@material-ui/core/Box'
import Header from 'ui/modules/header'
import Sidebar from 'ui/modules/sidebar'
import Main from 'ui/modules/main'

import ModalManager from 'containers/ModalManager'
import SnackbarManager from 'containers/SnackbarManager'
import Notifications from 'containers/Notifications'

const App = () => {
  const dispatch = useDispatch()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [anchorNotifications, setAnchorNotifications] = useState(null)
  const unreadNotifications = useSelector(getUnreadNotifications)
  const history = useHistory()
  const { pathname } = useLocation()

  useEffect(() => {
    const main = document.querySelector('main')
    if (main) {
      main.scrollTop = 0
      setSidebarOpen(false)
    }
  }, [pathname])

  const handleClickNotifications = event => {
    setAnchorNotifications(event.currentTarget)
  }

  const handleCloseNotifications = () => {
    setAnchorNotifications(null)
  }

  const user = useSelector(getUser)
  const isConfigLoaded = useSelector(getIsConfigLoaded)

  const handleLoginClick = () => {
    redirectToLogin()
  }

  const handleLogout = () => {
    setIsLoggingOut(true)
    dispatch(logout())
  }

  useEffect(() => {
    if (user) {
      dispatch(initConfig())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (user) {
      dispatch(checkActiveModule())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    history.listen(handleCloseNotifications)

    initHotjar()
    initGA()
    history.listen(location => {
      // console.log("tracking page view: " + location.pathname);
      ReactGA.pageview(location.pathname)
    })
  }, [history])

  return isConfigLoaded && !isLoggingOut ? (
    <>
      <CookieAdvice />
      <ModalManager />
      <SnackbarManager />
      <Notifications
        anchorEl={anchorNotifications}
        onClose={handleCloseNotifications}
      />
      <Header
        open={sidebarOpen}
        setOpen={setSidebarOpen}
        isLogged={!!user}
        isLanding={!user}
        onLoginClick={handleLoginClick}
        onClickNotifications={handleClickNotifications}
        unreadNotifications={unreadNotifications}
      />
      <Sidebar
        open={sidebarOpen}
        setOpen={setSidebarOpen}
        user={user}
        onLogout={handleLogout}
      />
      <Main open={sidebarOpen} isLogged={!!user} isLanding={!user}>
        {user ? (
          // Logged routes
          <Switch>
            <Route path="/app" exact component={Dashboard} />
            <Route path="/app/searches" exact component={Searches} />
            <Route path="/app/searches/new" component={SearchDetails} />
            <Route path="/app/searches/:id" exact component={SearchDetails} />
            <Route path="/app/vendors" exact component={Vendors} />
            <Route
              path="/app/vendors/invitations"
              exact
              component={Invitations}
            />
            <Route path="/app/vendors/:id" exact component={VendorDetails} />
            <Route path="/404" exact component={NotFound} />
            <Route path="/500" exact component={ServerError} />
            <Route path="/aerce" exact component={HomePageAerce} />
            <Route path="/" exact render={() => <Redirect to="/app" />} />
            <Route component={NotFound} />
          </Switch>
        ) : (
          // Not logged routes
          <Switch>
            <Route path="/aerce" exact component={HomePageAerce} />
            <Route path="/" exact component={HomePage} />
            <Route path="/login" exact component={Login} />
            <Route path="/app" render={() => <Redirect to="/" />} />
            <Route path="/404" exact component={NotFound} />
            <Route path="/500" exact component={ServerError} />
            <Route component={NotFound} />
          </Switch>
        )}
      </Main>
    </>
  ) : (
    <Box
      height="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <CircularProgress color="secondary" />
    </Box>
  )
}

export default App
